import { useState } from "react";
import { Sparkles, MapPin, Users, DollarSign, Clock, Globe, Pencil } from "lucide-react";

const DIMENSION_META = [
  { key: "expat_density", label: "Expat Density", Icon: Users },
  { key: "cost_of_living", label: "Cost of Living", Icon: DollarSign },
  { key: "pace_of_life", label: "Pace of Life", Icon: Clock },
  { key: "connectivity", label: "Connectivity", Icon: Globe },
];

const VIBE_OPTIONS = [
  {
    value: "relaxed",
    label: "Relaxed & authentic",
    pick: "alternative",
    blurb: "slower pace, lower cost, more local",
  },
  {
    value: "social",
    label: "Social & connected",
    pick: "headliner",
    blurb: "faster pace, richer expat network, international hub",
  },
];

/**
 * Renders the Comparative Regional Analysis (Vibe Check) section.
 * The LLM supplies a default pick; users can override via the "personalise" toggle.
 */
export const RegionalVibeCheck = ({ regional }) => {
  const [userPicked, setUserPicked] = useState(null); // null | 'headliner' | 'alternative'
  const [showToggle, setShowToggle] = useState(false);

  if (!regional || !regional.headliner || !regional.alternative) return null;

  const { headliner, alternative, recommendation } = regional;
  const llmPick = recommendation?.pick;
  const effectivePick = userPicked ?? llmPick;
  const hasUserOverride = userPicked !== null;

  const pickedLocationName =
    effectivePick === "headliner"
      ? headliner.location
      : effectivePick === "alternative"
      ? alternative.location
      : null;

  const currentOption =
    VIBE_OPTIONS.find((o) => o.pick === effectivePick) ?? null;

  const handleVibeChange = (pick) => {
    setUserPicked(pick);
  };

  return (
    <div
      className="bg-card rounded-2xl border border-primary/10 p-6 md:p-8 mb-6"
      data-testid="regional-vibe-check"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-5 w-5 text-secondary" />
        <h3 className="text-xl font-bold text-primary">
          Comparative Regional Analysis
        </h3>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Two real places, two different vibes — pick the one that fits your life.
      </p>

      {/* Side by side headers */}
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <LocationCard
          label="Headliner"
          tone="primary"
          isPick={effectivePick === "headliner"}
          location={headliner.location}
          description={headliner.description}
        />
        <LocationCard
          label="Alternative"
          tone="secondary"
          isPick={effectivePick === "alternative"}
          location={alternative.location}
          description={alternative.description}
        />
      </div>

      {/* Vibe Check table */}
      <div className="rounded-xl border border-primary/10 overflow-hidden">
        <div className="grid grid-cols-3 bg-primary text-white">
          <div className="p-3 text-xs font-semibold uppercase tracking-wider">
            Vibe Check
          </div>
          <div className="p-3 text-xs font-semibold uppercase tracking-wider text-center border-l border-white/10">
            {headliner.location}
          </div>
          <div className="p-3 text-xs font-semibold uppercase tracking-wider text-center border-l border-white/10">
            {alternative.location}
          </div>
        </div>
        {DIMENSION_META.map(({ key, label, Icon }, i) => (
          <div
            key={key}
            className={`grid grid-cols-3 ${i % 2 === 0 ? "bg-primary/5" : "bg-card"}`}
            data-testid={`vibe-row-${key}`}
          >
            <div className="p-3 flex items-center gap-2 text-sm font-medium text-primary">
              <Icon className="h-4 w-4 text-muted-foreground" />
              {label}
            </div>
            <div
              className={`p-3 text-sm text-center border-l border-primary/10 ${
                effectivePick === "headliner"
                  ? "text-primary font-semibold bg-accent/5"
                  : "text-primary/80"
              }`}
            >
              {headliner[key] || "—"}
            </div>
            <div
              className={`p-3 text-sm text-center border-l border-primary/10 ${
                effectivePick === "alternative"
                  ? "text-primary font-semibold bg-accent/5"
                  : "text-primary/80"
              }`}
            >
              {alternative[key] || "—"}
            </div>
          </div>
        ))}
      </div>

      {/* Recommendation + personalise toggle */}
      {effectivePick && (
        <div
          className="mt-5 rounded-xl bg-accent/10 border border-accent/30 p-4"
          data-testid="regional-recommendation"
        >
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-accent mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-primary mb-1">
                {hasUserOverride ? "Your pick" : "Our pick for you"}:{" "}
                <span data-testid="regional-picked-location">
                  {pickedLocationName}
                </span>
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {hasUserOverride && currentOption
                  ? `You chose the ${currentOption.label.toLowerCase()} vibe — ${currentOption.blurb}. ${pickedLocationName} fits that best.`
                  : recommendation?.reason}
              </p>
              {!showToggle && (
                <button
                  type="button"
                  onClick={() => setShowToggle(true)}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-secondary hover:underline"
                  data-testid="vibe-personalise-toggle-btn"
                >
                  <Pencil className="h-3 w-3" />
                  Which vibe fits you? Personalise this pick
                </button>
              )}
            </div>
          </div>

          {showToggle && (
            <div
              className="mt-4 pt-4 border-t border-accent/30 slide-in"
              data-testid="vibe-personalise-panel"
            >
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                What matters most to you right now?
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                {VIBE_OPTIONS.map((opt) => (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => handleVibeChange(opt.pick)}
                    className={`text-left rounded-lg p-3 border-2 transition-colors ${
                      effectivePick === opt.pick
                        ? "border-accent bg-accent/10"
                        : "border-primary/10 bg-card hover:border-primary/30"
                    }`}
                    data-testid={`vibe-option-${opt.value}`}
                  >
                    <p className="font-semibold text-primary text-sm">
                      {opt.label}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {opt.blurb}
                    </p>
                  </button>
                ))}
              </div>
              {hasUserOverride && (
                <button
                  type="button"
                  onClick={() => setUserPicked(null)}
                  className="mt-3 text-xs text-muted-foreground hover:text-primary underline"
                  data-testid="vibe-reset-btn"
                >
                  Reset to our recommendation
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const LocationCard = ({ label, tone, isPick, location, description }) => (
  <div
    className={`rounded-xl p-4 border-2 transition-colors ${
      isPick
        ? "border-accent bg-accent/5"
        : tone === "primary"
        ? "border-primary/20 bg-primary/5"
        : "border-secondary/20 bg-secondary/5"
    }`}
  >
    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
      {label}
      {isPick && (
        <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-accent text-white text-[10px] font-semibold">
          Best fit
        </span>
      )}
    </p>
    <h4 className="text-lg font-bold text-primary mb-1">{location}</h4>
    {description && (
      <p className="text-sm text-muted-foreground leading-relaxed">
        {description}
      </p>
    )}
  </div>
);

export default RegionalVibeCheck;
